import {Navigate, useLocation} from 'react-router-dom';
import type {ReactNode} from 'react';
import {useAuth} from '@shared/api/auth';
import {PageSkeleton} from '@shared/ui/primitives/PageSkeleton';

/**
 * Guard для защищённых роутов. Используется в router/index.tsx как обёртка
 * корневого роута. Логика:
 * - isLoading → скелетон (валидируем /me для уже-сохранённой сессии)
 * - !isAuthenticated → `<Navigate to="/login" />`, сохраняя текущий путь в state.from
 * - иначе — рендерим children как есть.
 *
 * Логика редиректа специально живёт здесь, а не в AuthProvider, потому что
 * провайдер стоит выше RouterProvider в `App.tsx` (см. комментарий в AuthProvider).
 */
export const RequireAuth = ({children}: {children: ReactNode}) => {
  const {isLoading, isAuthenticated} = useAuth();
  const location = useLocation();

  if (isLoading) return <PageSkeleton />;
  if (!isAuthenticated) {
    return (
      <Navigate
        to='/login'
        replace
        state={{from: location.pathname + location.search}}
      />
    );
  }
  return <>{children}</>;
};
